import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Wifi, WifiOff, Zap, FileText, CreditCard, Phone, LogOut, ChevronRight, ChevronLeft, Clock, CheckCircle, AlertTriangle, XCircle, ExternalLink, X, Gauge, Bell, User, Home, Share2, Calendar, Filter } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API, timeout: 15000 });
api.interceptors.request.use((cfg) => { const t = localStorage.getItem('cliente-token'); if (t) cfg.headers.Authorization = `Bearer ${t}`; return cfg; });

// ============= LOGIN =============
function LoginView({ onLogin }) {
  const [id, setId] = useState('');
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState(null);
  const go = async (providerId) => {
    if (!id.trim()) return toast.error('Digite seu telefone ou usuário');
    setLoading(true);
    try {
      const payload = { identifier: id.trim() };
      if (providerId) payload.provider_id = providerId;
      const r = await api.post('/api/mobile/login', payload);
      if (r.data.requires_provider_selection) { setProviders(r.data.providers); setLoading(false); return; }
      localStorage.setItem('cliente-token', r.data.token); onLogin(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao entrar'); }
    finally { setLoading(false); }
  };
  const selectProvider = async (p) => {
    setLoading(true);
    try { const r = await api.post('/api/mobile/login', { identifier: id.trim(), provider_id: p.user_id }); localStorage.setItem('cliente-token', r.data.token); onLogin(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-[#060a13] flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl overflow-hidden"><img src="/logo192.png" alt="Logo" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} /></div>
          <h1 className="text-2xl font-bold text-white">NexuZap <span className="text-emerald-500">Cliente</span></h1>
          <p className="text-sm text-slate-500 mt-1">Acesse sua conta</p>
        </div>
        <div className="space-y-4">
          <input className="w-full px-4 py-3.5 bg-[#0d1829] border border-[#1a2d4a] rounded-xl text-white placeholder-slate-600 focus:border-emerald-500 focus:outline-none transition-colors" placeholder="Telefone ou usuário" value={id} onChange={(e) => setId(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && go()} data-testid="cliente-login-input" />
          <button className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50" onClick={() => go()} disabled={loading} data-testid="cliente-login-btn">{loading ? 'Entrando...' : 'Entrar'}</button>
        </div>
        <p className="text-center text-xs text-slate-600">Dados fornecidos pelo seu provedor</p>

        {/* Provider Selection */}
        {providers && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setProviders(null)}>
            <div className="bg-[#0d1829] border border-emerald-500/20 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-white text-center mb-1">Escolha seu Provedor</h3>
              <p className="text-xs text-slate-500 text-center mb-4">Você está cadastrado em mais de um painel</p>
              <div className="space-y-2">
                {providers.map((p, i) => (
                  <button key={i} onClick={() => selectProvider(p)} className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#060a13] border border-[#1a2d4a] hover:border-emerald-500/30 transition-colors text-left">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <span className="text-lg font-bold text-emerald-500">{(p.provider_name || 'P')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1"><p className="text-sm font-semibold text-white">{p.provider_name}</p>{p.plan_name && <p className="text-xs text-slate-500">{p.plan_name}</p>}</div>
                    <ChevronRight className="w-4 h-4 text-emerald-500" />
                  </button>
                ))}
              </div>
              <button onClick={() => setProviders(null)} className="w-full mt-3 py-2 text-sm text-slate-500">Cancelar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============= BACK BUTTON =============
function BackBtn({ onClick, label }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0d1829] border border-[#1a2d4a] hover:border-emerald-500/30 transition-colors text-sm text-slate-400 hover:text-white mb-4">
      <ChevronLeft className="w-4 h-4" />{label || 'Voltar'}
    </button>
  );
}

// ============= DASHBOARD =============
function DashboardView({ data, onNavigate }) {
  const [showDays, setShowDays] = useState(false);
  const [bannerIdx, setBannerIdx] = useState(0);

  const parseDate = (d) => { if (!d) return null; if (d.includes('/')) { const [day, month, year] = d.split('/'); return new Date(year, month - 1, day); } return new Date(d + 'T12:00:00'); };
  const expDate = parseDate(data.expiration_date);
  const days = data.days_remaining ?? data.days_until_expiration ?? (expDate ? Math.ceil((expDate - new Date()) / 86400000) : 0);
  const s = data.deleted ? { c: 'red', l: 'Cancelado', Icon: XCircle } : data.status === 'expired' ? { c: 'red', l: 'Expirado', Icon: XCircle } : data.status === 'today' ? { c: 'red', l: 'Vence Hoje', Icon: AlertTriangle } : data.status === 'expiring' ? { c: 'yellow', l: 'Expirando', Icon: AlertTriangle } : { c: 'emerald', l: 'Ativo', Icon: CheckCircle };

  // Banner auto-carousel
  useEffect(() => {
    if (!data.app_banners?.length || data.app_banners.length <= 1) return;
    const t = setInterval(() => setBannerIdx(p => (p + 1) % data.app_banners.length), 4000);
    return () => clearInterval(t);
  }, [data.app_banners?.length]);

  return (
    <div className="space-y-4">
      {/* Fixed Notice */}
      {data.app_notice && (
        <div className="px-4 py-2.5 rounded-2xl bg-yellow-500/5 border border-yellow-500/15 text-center">
          <p className="text-xs font-semibold text-yellow-500">⚠ {data.app_notice}</p>
        </div>
      )}

      {/* Plan Card */}
      <div className={`relative overflow-hidden rounded-2xl border ${s.c === 'red' ? 'bg-red-500/5 border-red-500/15' : s.c === 'yellow' ? 'bg-yellow-500/5 border-yellow-500/15' : 'bg-emerald-500/5 border-emerald-500/15'}`}>
        <div className={`h-1 ${s.c === 'red' ? 'bg-red-500' : s.c === 'yellow' ? 'bg-yellow-500' : 'bg-emerald-500'}`} />
        <div className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-slate-500 bg-[#0d1829] px-2.5 py-1 rounded-lg">{data.plan_name}</span>
                <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg ${s.c === 'red' ? 'bg-red-500/10 text-red-400' : s.c === 'yellow' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.c === 'red' ? 'bg-red-400' : s.c === 'yellow' ? 'bg-yellow-400' : 'bg-emerald-400'}`} /> {s.l}
                </span>
              </div>
              <p className="text-3xl font-black text-emerald-500">R$ {data.active_coupon ? ((data.plan_price || 0) * (1 - data.active_coupon.discount_percent / 100)).toFixed(2) : (data.plan_price || 0).toFixed(2)}</p>
              {data.active_coupon && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-slate-500 line-through">R$ {(data.plan_price || 0).toFixed(2)}</span>
                  <span className="text-[10px] font-bold text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-md">🎟️ {data.active_coupon.code} -{data.active_coupon.discount_percent}%</span>
                </div>
              )}
              <p className="text-xs text-slate-600 mt-1">por mês</p>
            </div>
            <button onClick={() => setShowDays(true)} className={`flex flex-col items-center justify-center w-16 h-16 rounded-2xl border transition-transform hover:scale-105 ${s.c === 'red' ? 'border-red-500/20 bg-red-500/5' : s.c === 'yellow' ? 'border-yellow-500/20 bg-yellow-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`}>
              <span className={`text-xl font-black ${s.c === 'red' ? 'text-red-400' : s.c === 'yellow' ? 'text-yellow-400' : 'text-emerald-400'}`}>{Math.abs(days)}</span>
              <span className="text-[9px] text-slate-500 font-semibold">{days >= 0 ? 'dias' : 'vencido'}</span>
            </button>
          </div>
          <div className="mt-4 flex items-center justify-between text-[11px] text-slate-500">
            <span>Vencimento</span>
            <span className="font-semibold text-white">{expDate ? expDate.toLocaleDateString('pt-BR') : data.expiration_date || '-'}</span>
          </div>
          <div className="mt-2 h-1.5 bg-[#0d1829] rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${s.c === 'red' ? 'bg-red-500' : s.c === 'yellow' ? 'bg-yellow-500' : 'bg-emerald-500'}`} style={{ width: `${Math.max(0, Math.min(100, (days / 30) * 100))}%` }} />
          </div>
        </div>
      </div>

      {/* Days Popup */}
      {showDays && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-in fade-in" onClick={() => setShowDays(false)}>
          <div className="bg-[#0d1829] border border-[#1a2d4a] rounded-2xl p-6 w-72 text-center animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
            <div className={`w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center ${s.c === 'red' ? 'bg-red-500/10' : s.c === 'yellow' ? 'bg-yellow-500/10' : 'bg-emerald-500/10'}`}>
              <span className={`text-2xl font-black ${s.c === 'red' ? 'text-red-400' : s.c === 'yellow' ? 'text-yellow-400' : 'text-emerald-400'}`}>{Math.abs(days)}</span>
            </div>
            <p className="text-white font-bold">{days >= 0 ? 'Dias Restantes' : 'Dias Vencido'}</p>
            <p className="text-sm text-slate-500 mt-1">Vencimento: <span className="text-white font-semibold">{expDate ? expDate.toLocaleDateString('pt-BR') : data.expiration_date}</span></p>
            <button onClick={() => setShowDays(false)} className={`mt-4 px-6 py-2 rounded-xl text-sm font-semibold ${s.c === 'red' ? 'bg-red-500/10 text-red-400' : s.c === 'yellow' ? 'bg-yellow-500/10 text-yellow-400' : 'bg-emerald-500/10 text-emerald-400'}`}>Entendi</button>
          </div>
        </div>
      )}

      {/* DELETED STATE - Conta Cancelada */}
      {data.deleted && (
        <div className="flex flex-col items-center p-6 rounded-2xl bg-red-500/5 border border-red-500/20 text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-3">
            <span className="text-3xl">🚫</span>
          </div>
          <p className="text-base font-bold text-red-400 mb-1">Conta Cancelada</p>
          <p className="text-xs text-slate-500 leading-relaxed">Sua conta foi removida pelo seu provedor. Você tem até 30 dias para contatar o suporte.</p>
        </div>
      )}

      {/* Expired Alert - hidden when deleted */}
      {!data.deleted && (data.status === 'expired' || data.status === 'today') && data.gateway_available && (
        <button onClick={() => onNavigate('payment')} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-500/5 border border-red-500/20 hover:border-red-500/40 transition-colors text-left">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5 text-red-400" /></div>
          <div className="flex-1"><p className="text-sm font-bold text-red-400">{data.status === 'today' ? 'Vence Hoje' : 'Plano Expirado'}</p><p className="text-xs text-slate-500">Toque para renovar</p></div>
          <ChevronRight className="w-4 h-4 text-red-400" />
        </button>
      )}

      {/* Quick Actions - hidden when deleted */}
      {!data.deleted && (
      <>
      <div className="grid grid-cols-3 gap-3">
        {data.gateway_available && data.payment_link_enabled && (
          <button onClick={() => onNavigate('payment')} className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] hover:border-emerald-500/30 transition-colors" data-testid="cliente-pix-btn">
            <CreditCard className="w-5 h-5 text-emerald-500" /><span className="text-[11px] text-slate-400 font-medium">Pagar PIX</span>
          </button>
        )}
        <button onClick={() => onNavigate('invoices')} className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] hover:border-blue-500/30 transition-colors" data-testid="cliente-invoices-btn">
          <FileText className="w-5 h-5 text-blue-500" /><span className="text-[11px] text-slate-400 font-medium">Faturas</span>
        </button>
        <button onClick={() => onNavigate('speedtest')} className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] hover:border-purple-500/30 transition-colors" data-testid="cliente-speed-btn">
          <Gauge className="w-5 h-5 text-purple-500" /><span className="text-[11px] text-slate-400 font-medium">Velocidade</span>
        </button>
      </div>

      {/* Cupom + Presente + Avalie + Indicação */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button onClick={() => onNavigate('coupon')} className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] hover:border-orange-500/30 transition-colors">
          <span className="text-xl">🎟️</span><span className="text-[11px] text-slate-400 font-medium">Cupom</span>
        </button>
        {data.birthday_gift?.available && !data.birthday_gift?.claimed && (
          <button onClick={() => onNavigate('birthday')} className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-pink-500/20 hover:border-pink-500/40 transition-colors animate-pulse">
            <span className="text-xl">🎁</span><span className="text-[11px] text-pink-400 font-medium">Presente</span>
          </button>
        )}
        {data.referral?.enabled && !data.referrer_id && !data.referral?.my_pending_referrer && (
          <button onClick={() => onNavigate('referral-received')} className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-purple-500/20 hover:border-purple-500/40 transition-colors">
            <span className="text-xl">🤝</span><span className="text-[11px] text-purple-400 font-medium">Indicado?</span>
          </button>
        )}
        {data.referral?.enabled && (
          <button onClick={() => onNavigate('referral-share')} className="relative flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-blue-500/20 hover:border-blue-500/40 transition-colors">
            <span className="text-xl">👥</span><span className="text-[11px] text-blue-400 font-medium">Indicar</span>
            {data.referral?.pending_confirmations?.length > 0 && <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full" />}
          </button>
        )}
        <a href="https://play.google.com/store/apps/details?id=com.nexuzap.app&hl=pt_BR" target="_blank" rel="noopener noreferrer"
          className="flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] hover:border-yellow-500/30 transition-colors">
          <span className="text-xl">⭐</span><span className="text-[11px] text-slate-400 font-medium">Avalie</span>
        </a>
      </div>
      </>
      )}

      {/* WhatsApp Support */}
      {data.support_whatsapp && (
        <a href={`https://wa.me/${data.support_whatsapp}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] hover:border-green-500/30 transition-colors">
          <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center"><Phone className="w-5 h-5 text-green-500" /></div>
          <div className="flex-1"><p className="text-sm font-medium text-white">Suporte WhatsApp</p><p className="text-xs text-slate-500">Fale com seu provedor</p></div>
          <ChevronRight className="w-4 h-4 text-slate-600" />
        </a>
      )}

      {/* Banner Carousel */}
      {data.app_banners?.length > 0 && (
        <div className="relative rounded-2xl overflow-hidden border border-[#1a2d4a]">
          <div className="relative" style={{ paddingBottom: '50%' }}>
            {data.app_banners.map((b, i) => (
              <img key={b.id || i} src={b.url || b.full_url} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === bannerIdx ? 'opacity-100' : 'opacity-0'}`} />
            ))}
          </div>
          {data.app_banners.length > 1 && (
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
              {data.app_banners.map((_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === bannerIdx ? 'w-4 bg-emerald-500' : 'w-1.5 bg-slate-600'}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============= PROFILE VIEW =============
function ProfileView({ data, onBack }) {
  const s = data.status === 'expired' ? { c: 'text-red-400', bg: 'bg-red-500/10', l: 'Expirado' } : data.status === 'today' ? { c: 'text-red-400', bg: 'bg-red-500/10', l: 'Vence Hoje' } : data.status === 'expiring' ? { c: 'text-yellow-400', bg: 'bg-yellow-500/10', l: 'Expirando' } : { c: 'text-emerald-400', bg: 'bg-emerald-500/10', l: 'Ativo' };
  const parseDate = (d) => { if (!d) return '-'; if (d.includes('/')) return d; try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; } };

  const shareProfile = () => {
    const text = `═══ NexuZap Cliente ═══\nUsuário: ${data.username || data.phone || '—'}\nNome: ${data.name}\nTelefone: ${data.phone}\nPlano: ${data.plan_name}\nStatus: ${s.l}\nVencimento: ${parseDate(data.expiration_date)}\nProvedor: ${data.owner_name || '—'}\n═══════════════════════`;
    if (navigator.share) { navigator.share({ text }).catch(() => {}); }
    else { navigator.clipboard.writeText(text); toast.success('Copiado!'); }
  };

  const rows = [
    { l: 'Usuário', v: data.username || data.phone, icon: User },
    { l: 'Telefone', v: data.phone, icon: Phone },
    { l: 'Status', v: s.l, icon: CheckCircle, vc: s.c },
    { l: 'Plano', v: data.plan_name, icon: CreditCard },
    { l: 'Vencimento', v: parseDate(data.expiration_date), icon: Calendar },
    ...(data.owner_name ? [{ l: 'Provedor', v: data.owner_name, icon: ExternalLink }] : []),
  ];

  return (
    <div className="space-y-4">
      {/* Avatar */}
      <div className="flex flex-col items-center pt-6 pb-2">
        <div className="w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-500/20 flex items-center justify-center mb-3">
          <span className="text-3xl font-bold text-emerald-500">{(data.name || 'U')[0].toUpperCase()}</span>
        </div>
        <h2 className="text-xl font-bold text-white">{data.name}</h2>
        <p className="text-sm text-slate-500">{data.phone}</p>
      </div>

      {/* Details */}
      <div className="rounded-2xl bg-[#0d1829] border border-[#1a2d4a] divide-y divide-[#1a2d4a]">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-3 px-4 py-3.5">
            <r.icon className="w-4 h-4 text-slate-600 shrink-0" />
            <span className="text-sm text-slate-500 flex-1">{r.l}</span>
            <span className={`text-sm font-semibold ${r.vc || 'text-white'}`}>{r.v}</span>
          </div>
        ))}
      </div>

      {/* Share */}
      <button onClick={shareProfile} className="w-full flex items-center justify-center gap-2 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors">
        <Share2 className="w-4 h-4" /> Compartilhar Cadastro
      </button>

      {/* Switch Provider */}
      <button onClick={async () => {
        try {
          const r = await api.get('/api/mobile/providers');
          if (r.data?.length > 1) {
            const others = r.data.filter(p => !p.is_current);
            if (others.length > 0) {
              // Show provider selection
              const el = document.createElement('div');
              el.id = 'provider-modal';
              el.className = 'fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4';
              el.innerHTML = `<div class="bg-[#0d1829] border border-[#1a2d4a] rounded-2xl p-6 w-full max-w-sm"><h3 class="text-lg font-bold text-white text-center mb-4">Trocar Provedor</h3><div class="space-y-2" id="prov-list"></div><button onclick="document.getElementById('provider-modal').remove()" class="w-full mt-3 py-2 text-sm text-slate-500">Cancelar</button></div>`;
              document.body.appendChild(el);
              const list = document.getElementById('prov-list');
              others.forEach(p => {
                const btn = document.createElement('button');
                btn.className = 'w-full flex items-center gap-3 p-4 rounded-xl bg-[#060a13] border border-[#1a2d4a] hover:border-emerald-500/30 transition-colors text-left';
                btn.innerHTML = `<div class="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0"><span class="text-lg font-bold text-emerald-500">${(p.provider_name||'P')[0].toUpperCase()}</span></div><div class="flex-1"><p class="text-sm font-semibold text-white">${p.provider_name}</p>${p.plan_name ? `<p class="text-xs text-slate-500">${p.plan_name}</p>` : ''}</div>`;
                btn.onclick = async () => {
                  try {
                    const sw = await api.post('/api/mobile/switch-provider', { customer_id: p.customer_id });
                    localStorage.setItem('cliente-token', sw.data.token);
                    window.location.reload();
                  } catch { toast.error('Erro ao trocar'); }
                };
                list.appendChild(btn);
              });
            }
          } else { toast.info('Você está cadastrado em apenas um provedor.'); }
        } catch { toast.error('Erro ao carregar provedores'); }
      }} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-[#1a2d4a] text-sm text-slate-400 hover:text-white hover:border-emerald-500/30 transition-colors">
        🔄 Trocar Provedor
      </button>
    </div>
  );
}

// ============= PAYMENT =============
function PaymentView({ data, onBack }) {
  const [loading, setLoading] = useState(false);
  const [pix, setPix] = useState(null);
  const plansToChoose = (!data.has_custom_price && (data.available_plans || []).length > 1) ? data.available_plans : [];
  const [planId, setPlanId] = useState(data.plan_id);
  const chosen = plansToChoose.find((p) => p.id === planId);
  const price = chosen ? chosen.price : (data.plan_price || 0);
  const generate = async () => {
    setLoading(true);
    try {
      const qs = chosen && planId !== data.plan_id ? `?plan_id=${encodeURIComponent(planId)}` : '';
      const r = await api.post(`/api/mobile/generate-pix${qs}`);
      setPix(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar PIX'); } finally { setLoading(false); }
  };
  const copyCode = () => { const code = pix?.brcode || pix?.qr_code_text; if (code) { navigator.clipboard.writeText(code); toast.success('Código PIX copiado!'); } };
  return (
    <div className="space-y-4">
      <BackBtn onClick={onBack} />
      <div className="p-5 rounded-2xl bg-[#0d1829] border border-[#1a2d4a]">
        <h2 className="text-lg font-bold text-white mb-1">Pagamento via PIX</h2>
        {plansToChoose.length > 0 && !pix && (
          <div className="mb-4 space-y-2">
            <p className="text-xs text-slate-500">Escolha o plano da renovação:</p>
            {plansToChoose.map((p) => (
              <button key={p.id} type="button" onClick={() => setPlanId(p.id)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl border text-left transition-colors ${planId === p.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-[#1a2d4a] hover:border-emerald-500/40'}`}>
                <span>
                  <span className="block text-sm font-semibold text-white">{p.name}</span>
                  <span className="block text-xs text-slate-500">{p.duration_months} {p.duration_months === 1 ? 'mês' : 'meses'}{p.is_current ? ' · atual' : ''}</span>
                </span>
                <span className="text-sm font-bold text-emerald-500">R$ {Number(p.price || 0).toFixed(2)}</span>
              </button>
            ))}
          </div>
        )}
        {data.active_coupon ? (
          <div className="mb-4">
            <p className="text-sm text-slate-500">{chosen ? chosen.name : data.plan_name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-bold text-emerald-500">R$ {(price * (1 - data.active_coupon.discount_percent / 100)).toFixed(2)}</span>
              <span className="text-sm text-slate-600 line-through">R$ {price.toFixed(2)}</span>
              <span className="text-[10px] font-bold text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded">🎟️ -{data.active_coupon.discount_percent}%</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-500 mb-4">{chosen ? chosen.name : data.plan_name} — R$ {price.toFixed(2)}</p>
        )}
        {!pix ? (
          <button onClick={generate} disabled={loading} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl disabled:opacity-50 transition-colors">{loading ? 'Gerando...' : 'Gerar QR Code PIX'}</button>
        ) : (
          <div className="space-y-4 text-center">
            {(pix.qr_code_base64 || pix.qr_code) && <img src={`data:image/png;base64,${pix.qr_code_base64 || pix.qr_code}`} alt="QR" className="w-48 h-48 mx-auto rounded-xl bg-white p-2" />}
            {(pix.brcode || pix.qr_code_text) && (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 break-all bg-[#060a13] p-3 rounded-lg font-mono">{pix.brcode || pix.qr_code_text}</p>
                <button onClick={copyCode} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors">Copiar Código PIX</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============= INVOICES =============
function InvoicesView({ onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterMonth, setFilterMonth] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [selected, setSelected] = useState(null);

  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  const currentYear = new Date().getFullYear();

  const fetchInvoices = async (p = 1, filter = filterMonth) => {
    setLoading(true);
    try {
      let url = `/api/mobile/invoices?page=${p}`;
      if (filter) url += `&month=${filter.month}&year=${filter.year}`;
      const r = await api.get(url);
      setItems(r.data.items || r.data || []);
      setTotalPages(r.data.pages || 1);
      setPage(r.data.page || p);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchInvoices(); }, []);

  const fd = (d) => { try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d || '-'; } };
  const fdt = (d) => { try { const dt = new Date(d); return dt.toLocaleDateString('pt-BR') + ' às ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }); } catch { return d || '-'; } };

  const shareReceipt = (item) => {
    const text = `═══════════════════════\n    NEXUZAP - COMPROVANTE\n═══════════════════════\n\nCliente: ${item.customer_name || '—'}\nPlano: ${item.plan_name || 'Renovação'}\nValor: R$ ${(item.amount || 0).toFixed(2)}\nData: ${fdt(item.paid_at || item.created_at)}\nProvedor: ${item.provider_name || '—'}\n\nStatus: ✅ PAGO\nID: ${(item.id || '').substring(0, 8)}\n\n═══════════════════════\n    NexuZap - Sistema Oficial\n═══════════════════════`;
    if (navigator.share) { navigator.share({ text }).catch(() => {}); }
    else { navigator.clipboard.writeText(text); toast.success('Copiado!'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <BackBtn onClick={onBack} />
        <button onClick={() => setShowFilter(!showFilter)} className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold mb-4 border transition-colors ${filterMonth ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-[#0d1829] border-[#1a2d4a] text-slate-400'}`}>
          <Filter className="w-3.5 h-3.5" /> {filterMonth ? `${months[filterMonth.month - 1]}/${filterMonth.year}` : 'Filtrar'}
        </button>
      </div>

      {showFilter && (
        <div className="p-3 rounded-2xl bg-[#0d1829] border border-[#1a2d4a]">
          <div className="flex flex-wrap gap-2">
            {months.map((m, i) => (
              <button key={i} onClick={() => { setFilterMonth({ month: i + 1, year: currentYear }); setShowFilter(false); fetchInvoices(1, { month: i + 1, year: currentYear }); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${filterMonth?.month === i + 1 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-[#060a13] border-[#1a2d4a] text-slate-400 hover:text-white'}`}>{m}</button>
            ))}
          </div>
          {filterMonth && <button onClick={() => { setFilterMonth(null); setShowFilter(false); fetchInvoices(1, null); }} className="text-xs text-red-400 mt-2">Limpar filtro</button>}
        </div>
      )}

      <h2 className="text-base font-bold text-white">Faturas Pagas</h2>
      {loading ? <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
       : items.length === 0 ? <p className="text-sm text-slate-500 text-center py-8">{filterMonth ? 'Nenhuma fatura neste mês' : 'Nenhuma fatura paga'}</p>
       : (
        <>
          <div className="space-y-2">
            {items.map((inv, i) => (
              <button key={inv.id || i} onClick={() => setSelected(inv)} className="w-full p-4 rounded-xl bg-[#0d1829] border border-[#1a2d4a] hover:border-emerald-500/20 flex items-center justify-between text-left transition-colors">
                <div><p className="text-sm font-medium text-white">{inv.plan_name || inv.description || 'Renovação'}</p><p className="text-xs text-slate-500">{fd(inv.paid_at || inv.created_at)}</p></div>
                <div className="text-right"><p className="text-sm font-bold text-white">R$ {(inv.amount || 0).toFixed(2)}</p><span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-semibold">Pago</span></div>
              </button>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 pt-2">
              <button disabled={page <= 1} onClick={() => fetchInvoices(page - 1)} className="text-sm text-emerald-400 disabled:opacity-30">‹ Anterior</button>
              <span className="text-xs text-slate-500">{page}/{totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => fetchInvoices(page + 1)} className="text-sm text-emerald-400 disabled:opacity-30">Próxima ›</button>
            </div>
          )}
        </>
      )}

      {/* Receipt Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#0a0f1a] border border-emerald-500/15 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-emerald-500/5 py-5 text-center border-b border-[#1a2d4a]">
              <img src="/logo192.png" alt="" className="w-12 h-12 mx-auto mb-2 rounded-xl" />
              <p className="text-emerald-500 font-black tracking-widest text-sm">NEXUZAP</p>
              <p className="text-[10px] text-slate-500 font-semibold tracking-[3px] mt-0.5">COMPROVANTE</p>
            </div>
            <div className="p-5 space-y-4 relative">
              <img src="/logo192.png" alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 opacity-[0.02]" />
              <div><p className="text-[10px] text-slate-600 font-bold tracking-wider uppercase">Plano</p><p className="text-white font-bold mt-0.5">{selected.plan_name || 'Renovação'}</p></div>
              <div><p className="text-[10px] text-slate-600 font-bold tracking-wider uppercase">Valor Pago</p><p className="text-2xl font-black text-emerald-500 mt-0.5">R$ {(selected.amount || 0).toFixed(2)}</p></div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-[10px] text-slate-600 font-bold tracking-wider uppercase">Data</p><p className="text-sm text-white font-semibold mt-0.5">{fdt(selected.paid_at || selected.created_at)}</p></div>
                <div><p className="text-[10px] text-slate-600 font-bold tracking-wider uppercase">Status</p><p className="text-sm text-emerald-400 font-bold mt-0.5">✓ Aprovado</p></div>
              </div>
              <div><p className="text-[10px] text-slate-600 font-bold tracking-wider uppercase">Provedor</p><p className="text-sm text-white font-semibold mt-0.5">{selected.provider_name || '—'}</p></div>
              <div className="border-t border-[#1a2d4a] pt-3"><p className="text-[10px] text-slate-600 text-center">ID: {(selected.id || '').substring(0, 8).toUpperCase()}</p></div>
            </div>
            <div className="flex border-t border-[#1a2d4a]">
              <button onClick={() => setSelected(null)} className="flex-1 py-3.5 text-sm text-slate-400 font-semibold border-r border-[#1a2d4a]">Fechar</button>
              <button onClick={() => shareReceipt(selected)} className="flex-1 py-3.5 text-sm text-emerald-400 font-bold bg-emerald-500/5">Compartilhar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============= SPEED TEST =============
function SpeedTestView({ onBack }) {
  const [ip, setIp] = useState(null);
  const [isp, setIsp] = useState(null);
  const [testing, setTesting] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [download, setDownload] = useState(0);
  const [upload, setUpload] = useState(0);
  const [ping, setPing] = useState(0);
  const [jitter, setJitter] = useState(0);
  const [liveSpeed, setLiveSpeed] = useState(0);

  useEffect(() => {
    fetch('https://ipapi.co/json/').then(r => r.json()).then(d => { setIp(d.ip || 'Indisponível'); setIsp(d.org || ''); })
      .catch(() => fetch('https://api.ipify.org?format=json').then(r => r.json()).then(d => setIp(d.ip)).catch(() => setIp('Indisponível')));
  }, []);

  const run = async () => {
    setTesting(true); setDownload(0); setUpload(0); setPing(0); setJitter(0); setLiveSpeed(0);
    try {
      setPhase('ping');
      const pings = [];
      for (let i = 0; i < 6; i++) { const s = Date.now(); await fetch(`https://speed.cloudflare.com/__down?bytes=0&_=${Date.now()}`, { cache: 'no-store' }); pings.push(Date.now() - s); }
      pings.sort((a, b) => a - b); const tp = pings.slice(1, -1);
      setPing(Math.round(tp.reduce((a, b) => a + b, 0) / tp.length));
      let js = 0; for (let i = 1; i < tp.length; i++) js += Math.abs(tp[i] - tp[i - 1]);
      setJitter(Math.round(js / Math.max(tp.length - 1, 1)));

      setPhase('download');
      await fetch(`https://speed.cloudflare.com/__down?bytes=500000&_=${Date.now()}`).then(r => r.arrayBuffer()).catch(() => {});
      let dlTotal = 0; const dlStart = Date.now();
      for (let r = 0; r < 4; r++) {
        await Promise.all(Array.from({ length: 4 }, (_, s) => fetch(`https://speed.cloudflare.com/__down?bytes=10000000&_=${Date.now()}_${r}_${s}`).then(res => res.arrayBuffer()).then(buf => { dlTotal += buf.byteLength; }).catch(() => {})));
        const el = (Date.now() - dlStart) / 1000; if (el > 0) setLiveSpeed(parseFloat(((dlTotal * 8) / (el * 1e6)).toFixed(1)));
      }
      setDownload((Date.now() - dlStart) / 1000 > 0 ? parseFloat(((dlTotal * 8) / (((Date.now() - dlStart) / 1000) * 1e6)).toFixed(2)) : 0);
      setLiveSpeed(0);

      setPhase('upload');
      await fetch('https://speed.cloudflare.com/__up', { method: 'POST', body: new ArrayBuffer(100000) }).catch(() => {});
      let ulTotal = 0; const ulStart = Date.now();
      for (let r = 0; r < 3; r++) {
        await Promise.all(Array.from({ length: 3 }, () => fetch('https://speed.cloudflare.com/__up', { method: 'POST', body: new ArrayBuffer(2000000) }).then(() => { ulTotal += 2000000; }).catch(() => {})));
        const el = (Date.now() - ulStart) / 1000; if (el > 0) setLiveSpeed(parseFloat(((ulTotal * 8) / (el * 1e6)).toFixed(1)));
      }
      setUpload((Date.now() - ulStart) / 1000 > 0 ? parseFloat(((ulTotal * 8) / (((Date.now() - ulStart) / 1000) * 1e6)).toFixed(2)) : 0);
      setLiveSpeed(0);
      setPhase('done');
    } catch { setPhase('idle'); toast.error('Erro no teste'); }
    finally { setTesting(false); }
  };

  const fmt = (v) => v >= 100 ? v.toFixed(0) : v.toFixed(1);
  const gaugeVal = phase === 'download' ? (liveSpeed || download) : phase === 'upload' ? (liveSpeed || upload) : download;
  const gaugeColor = phase === 'upload' ? 'text-purple-500 border-purple-500' : 'text-emerald-500 border-emerald-500';

  return (
    <div className="space-y-4">
      <BackBtn onClick={onBack} />
      <div className="p-6 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] text-center">
        {phase === 'idle' ? (
          <button onClick={run} className="w-32 h-32 mx-auto rounded-full border-[3px] border-emerald-500/30 bg-emerald-500/5 flex items-center justify-center mb-4 hover:border-emerald-500/60 transition-colors">
            <div><p className="text-xl font-black text-emerald-500 tracking-widest">GO</p><p className="text-[10px] text-slate-500 mt-0.5">Iniciar</p></div>
          </button>
        ) : phase === 'ping' ? (
          <div className="py-10"><div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto" /><p className="text-sm text-yellow-500 mt-4 font-semibold">Medindo Ping...</p></div>
        ) : (
          <div className={`w-40 h-40 mx-auto rounded-full border-[3px] flex items-center justify-center mb-4 ${phase === 'done' ? 'border-emerald-500/40 bg-emerald-500/5' : phase === 'upload' ? 'border-purple-500/40 bg-purple-500/5' : 'border-emerald-500/40 bg-emerald-500/5'}`}>
            <div>
              <p className={`text-3xl font-black ${phase === 'done' ? 'text-emerald-500' : phase === 'upload' ? 'text-purple-500' : 'text-emerald-500'}`}>{fmt(gaugeVal)}</p>
              <p className="text-xs text-slate-500 font-semibold">Mbps</p>
              <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider font-bold">{phase === 'download' ? 'Download' : phase === 'upload' ? 'Upload' : 'Download'}</p>
            </div>
          </div>
        )}
        {testing && phase !== 'ping' && (
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className={`w-2 h-2 rounded-full animate-pulse ${phase === 'download' ? 'bg-emerald-500' : 'bg-purple-500'}`} />
            <p className={`text-xs font-semibold ${phase === 'download' ? 'text-emerald-500' : 'text-purple-500'}`}>{phase === 'download' ? 'Download...' : 'Upload...'}</p>
          </div>
        )}
        {(phase === 'done' || download > 0) && (
          <>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10"><p className="text-[10px] text-slate-500 font-bold">DOWNLOAD</p><p className="text-xl font-black text-emerald-500">{fmt(download)}</p><p className="text-[10px] text-slate-500">Mbps</p></div>
              <div className="p-3 rounded-xl bg-purple-500/5 border border-purple-500/10"><p className="text-[10px] text-slate-500 font-bold">UPLOAD</p><p className="text-xl font-black text-purple-500">{fmt(upload)}</p><p className="text-[10px] text-slate-500">Mbps</p></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-2.5 rounded-xl bg-yellow-500/5 border border-yellow-500/10"><p className="text-[10px] text-slate-500 font-bold">PING</p><p className="text-lg font-black text-yellow-500">{ping}</p><p className="text-[10px] text-slate-500">ms</p></div>
              <div className="p-2.5 rounded-xl bg-red-500/5 border border-red-500/10"><p className="text-[10px] text-slate-500 font-bold">JITTER</p><p className="text-lg font-black text-red-500">{jitter}</p><p className="text-[10px] text-slate-500">ms</p></div>
            </div>
          </>
        )}
        {phase === 'done' && <button onClick={run} className="w-full py-3 mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors">Testar Novamente</button>}
      </div>
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a]">
        <Wifi className="w-5 h-5 text-blue-500 shrink-0" />
        <div><p className="text-sm font-bold text-white">{ip || '...'}</p>{isp && <p className="text-[10px] text-slate-500">{isp}</p>}</div>
      </div>
    </div>
  );
}



// ============= INDICAÇÃO - FUI INDICADO =============

function ReferralReceivedView({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refPhone, setRefPhone] = useState('');
  const [sending, setSending] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const token = localStorage.getItem('cliente-token');
    try {
      const me = await fetch(`${API_URL}/api/mobile/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      setData(me);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitReferrer = async () => {
    if (!refPhone.trim()) return;
    setSending(true);
    const token = localStorage.getItem('cliente-token');
    try {
      const r = await fetch(`${API_URL}/api/mobile/referral/set-referrer`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ phone: refPhone.trim() }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      alert(d.message || 'Enviado!'); setRefPhone(''); load();
    } catch (e) { alert(e.message || 'Erro'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  const ref = data?.referral;
  if (!ref?.enabled) return <div className="text-center py-12"><p className="text-slate-500 text-sm">Indicacao nao disponivel</p><button onClick={onBack} className="mt-4 text-emerald-400 text-sm">Voltar</button></div>;

  return (
    <div className="space-y-4">
      <BackBtn onClick={onBack} />
      <h2 className="text-lg font-bold text-white">Fui Indicado</h2>

      {!ref.referrer_id && !ref.my_pending_referrer && (
        <div className="p-4 rounded-2xl bg-[#0d1829] border border-purple-500/20">
          <p className="text-sm font-semibold text-purple-400 mb-1">Fui indicado por alguem</p>
          <p className="text-xs text-slate-400 mb-3">Informe o numero de quem te indicou</p>
          <input value={refPhone} onChange={e => setRefPhone(e.target.value)} placeholder="Numero do indicador" className="w-full p-2.5 rounded-lg bg-[#0a1020] border border-[#1a2d4a] text-white text-sm mb-3" />
          <button onClick={submitReferrer} disabled={sending} className="w-full py-2.5 text-sm font-bold text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50">{sending ? 'Enviando...' : 'Enviar'}</button>
        </div>
      )}
      {ref.my_pending_referrer && (
        <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/20"><p className="text-xs text-amber-400 font-medium">Aguardando {ref.my_pending_referrer} confirmar</p></div>
      )}
      {ref.referrer_id && (
        <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20"><p className="text-xs text-emerald-400 font-medium">Voce ja foi indicado!</p></div>
      )}
    </div>
  );
}

// ============= INDICAÇÃO - INDICAR AMIGOS =============

function ReferralShareView({ onBack }) {
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const token = localStorage.getItem('cliente-token');
    try {
      const [me, refs, pend] = await Promise.all([
        fetch(`${API_URL}/api/mobile/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API_URL}/api/mobile/referral/my-referrals`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []),
        fetch(`${API_URL}/api/mobile/referral/pending`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => [])
      ]);
      setData(me);
      setReferrals(Array.isArray(refs) ? refs : []);
      setPending(Array.isArray(pend) ? pend : []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirmRef = async (id) => {
    const token = localStorage.getItem('cliente-token');
    try {
      const r = await fetch(`${API_URL}/api/mobile/referral/confirm/${id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      alert(d.message || 'Confirmado!'); load();
    } catch (e) { alert(e.message || 'Erro'); }
  };
  const rejectRef = async (id) => {
    const token = localStorage.getItem('cliente-token');
    try {
      await fetch(`${API_URL}/api/mobile/referral/reject/${id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      load();
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  const ref = data?.referral;
  if (!ref?.enabled) return <div className="text-center py-12"><p className="text-slate-500 text-sm">Indicacao nao disponivel</p><button onClick={onBack} className="mt-4 text-emerald-400 text-sm">Voltar</button></div>;

  const prog = Math.min(1, (ref.count || 0) / (ref.required || 1));
  const bonusLabel = ref.bonus_type === 'free_month' ? `${ref.free_months || 1} mes gratis` : ref.bonus_type === 'discount_fixed' ? `R$ ${(ref.discount_value || 0).toFixed(2)} de desconto` : `${ref.discount_value || 0}% de desconto`;

  return (
    <div className="space-y-4">
      <BackBtn onClick={onBack} />
      <h2 className="text-lg font-bold text-white">Indicar Amigos</h2>

      {/* Progresso */}
      <div className="p-4 rounded-2xl bg-[#0d1829] border border-blue-500/20">
        <p className="text-sm font-semibold text-white mb-1">Seu Progresso</p>
        <p className="text-xs text-slate-400 mb-3">{ref.count || 0} de {ref.required} — Premio: {bonusLabel}</p>
        <div className="h-2 bg-[#1a2d4a] rounded-full"><div className="h-2 bg-blue-500 rounded-full" style={{ width: `${prog * 100}%` }} /></div>
      </div>

      {/* Como funciona */}
      <div className="p-4 rounded-2xl bg-[#0d1829] border border-[#1a2d4a]">
        <p className="text-sm font-semibold text-white mb-2">Como funciona?</p>
        <p className="text-xs text-slate-400">Seus amigos devem informar seu numero de telefone ao contratar. Voce recebera uma notificacao para confirmar a indicacao.</p>
        <p className="text-xs text-blue-400 mt-2 font-medium">Seu codigo: ultimos 4 digitos do seu telefone</p>
      </div>

      {/* Pendentes de confirmação */}
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-amber-400">Aguardando Confirmacao ({pending.length})</p>
          {pending.map(p => (
            <div key={p.id} className="p-3 rounded-xl bg-[#0d1829] border border-amber-500/20">
              <p className="text-sm font-medium text-white">{p.referred_name}</p>
              <p className="text-xs text-slate-400 mb-3">Diz que foi indicado por voce</p>
              <div className="flex gap-2">
                <button onClick={() => confirmRef(p.id)} className="flex-1 py-2 text-xs font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700">Confirmar</button>
                <button onClick={() => rejectRef(p.id)} className="flex-1 py-2 text-xs font-bold text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20">Recusar</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Indicações confirmadas */}
      <div>
        <p className="text-sm font-semibold text-white mb-2">Indicacoes Confirmadas ({referrals.length})</p>
        {referrals.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-4">Nenhuma indicacao confirmada</p>
        ) : referrals.map(r => (
          <div key={r.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0d1829] border border-[#1a2d4a] mb-2">
            <div><p className="text-sm font-medium text-white">{r.referred_name}</p><p className="text-[10px] text-slate-500">***{r.referred_phone}</p></div>
            <span className="text-[10px] text-emerald-400 font-semibold">Confirmada</span>
          </div>
        ))}
      </div>
    </div>
  );
}


// ============= CUPOM =============

function BirthdayView({ onBack }) {
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [giftResult, setGiftResult] = useState(null);

  const claimGift = async () => {
    setClaiming(true);
    try {
      const r = await api.post('/api/mobile/birthday-gift/claim');
      setGiftResult(r.data);
      setClaimed(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resgatar presente');
    } finally { setClaiming(false); }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-2">
        <ChevronRight className="w-4 h-4 rotate-180" /><span className="text-sm">Voltar</span>
      </button>

      {!claimed ? (
        <div className="flex flex-col items-center text-center px-4">
          <div className="w-32 h-32 rounded-full bg-pink-500/10 flex items-center justify-center mb-6 animate-bounce" style={{ animationDuration: '2s' }}>
            <span className="text-6xl">🎁</span>
          </div>
          <h2 className="text-2xl font-black text-white mb-2">Feliz Aniversário!</h2>
          <p className="text-sm text-slate-400 mb-8 leading-relaxed">
            Parabéns pelo seu dia especial! Preparamos um presente exclusivo para você.
          </p>
          <button onClick={claimGift} disabled={claiming}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold text-lg shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 transition-shadow disabled:opacity-50">
            {claiming ? 'Abrindo...' : 'Abrir Presente 🎉'}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center px-4 animate-fade-in">
          <div className="text-7xl mb-4">🎉</div>
          <div className="text-5xl mb-6">🥳</div>
          <h2 className="text-2xl font-black text-white mb-2">Presente Resgatado!</h2>
          <p className="text-sm text-slate-400 mb-6">{giftResult?.message}</p>
          <div className="w-full p-6 rounded-2xl bg-[#0d1829] border border-emerald-500/20">
            <span className="text-4xl block mb-3">{giftResult?.gift_type === 'coupon' ? '🎟️' : '🔄'}</span>
            <p className="text-base font-bold text-emerald-400">{giftResult?.gift_detail}</p>
          </div>
          <button onClick={onBack} className="mt-6 w-full py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-semibold hover:bg-emerald-500/20 transition-colors">
            Voltar
          </button>
        </div>
      )}
    </div>
  );
}


function CouponView({ onBack }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const apply = async () => {
    if (!code.trim()) return toast.error('Digite o código do cupom');
    setLoading(true);
    try {
      const r = await api.post('/api/mobile/apply-coupon', { code: code.trim() });
      setResult(r.data);
      toast.success('Cupom aplicado!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Cupom inválido'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <BackBtn onClick={onBack} />
      <div className="p-6 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] text-center">
        <p className="text-4xl mb-3">🎟️</p>
        <h2 className="text-lg font-bold text-white mb-1">Aplicar Cupom</h2>
        <p className="text-xs text-slate-500 mb-5">Digite o código do cupom recebido para obter desconto na próxima renovação</p>
        {result ? (
          <div className="space-y-3">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center"><p className="text-3xl">✅</p></div>
            <p className="text-lg font-bold text-emerald-400">Cupom Aplicado!</p>
            <p className="text-sm text-white font-semibold">{result.code} — {result.discount_percent}% de desconto</p>
            <p className="text-xs text-slate-400">{result.message}</p>
            <button onClick={onBack} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors">Voltar</button>
          </div>
        ) : (
          <div className="space-y-3">
            <input className="w-full px-4 py-3.5 bg-[#060a13] border border-[#1a2d4a] rounded-xl text-white text-center text-lg font-bold tracking-[4px] font-mono placeholder-slate-600 focus:border-orange-500 focus:outline-none transition-colors"
              placeholder="CÓDIGO" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={20} onKeyDown={(e) => e.key === 'Enter' && apply()} />
            <button onClick={apply} disabled={loading} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50">
              {loading ? 'Verificando...' : 'Aplicar Cupom'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ============= INSTALL GUIDE =============
function InstallGuideView({ onBack, installPrompt, onInstall }) {
  const [isIOS] = useState(/iPad|iPhone|iPod/.test(navigator.userAgent));
  return (
    <div className="space-y-4">
      <BackBtn onClick={onBack} />
      <h2 className="text-lg font-bold text-white">Instalar App</h2>
      {installPrompt ? (
        <div className="p-5 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] text-center space-y-4">
          <Zap className="w-12 h-12 text-emerald-500 mx-auto" />
          <p className="text-sm text-slate-400">Clique abaixo para instalar o app na tela inicial</p>
          <button onClick={onInstall} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl">Instalar Agora</button>
        </div>
      ) : isIOS ? (
        <div className="p-5 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] space-y-3">
          <p className="text-sm font-semibold text-white">Para iPhone/iPad:</p>
          <ol className="text-sm text-slate-400 space-y-2 list-decimal list-inside">
            <li>Toque no botão <strong className="text-white">Compartilhar</strong> (ícone de quadrado com seta)</li>
            <li>Role para baixo e toque em <strong className="text-white">Adicionar à Tela de Início</strong></li>
            <li>Toque em <strong className="text-white">Adicionar</strong></li>
          </ol>
        </div>
      ) : (
        <div className="p-5 rounded-2xl bg-[#0d1829] border border-[#1a2d4a] space-y-3">
          <p className="text-sm font-semibold text-white">Para Android:</p>
          <ol className="text-sm text-slate-400 space-y-2 list-decimal list-inside">
            <li>Toque no menu <strong className="text-white">⋮</strong> (3 pontos)</li>
            <li>Toque em <strong className="text-white">Instalar aplicativo</strong> ou <strong className="text-white">Adicionar à tela inicial</strong></li>
          </ol>
        </div>
      )}
    </div>
  );
}

// ============= NOTIFICATION MODAL =============
function NotificationModal({ notif, onClose }) {
  if (!notif) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0d1829] border border-emerald-500/20 rounded-2xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-white text-center mb-2">{notif.title}</h3>
        <p className="text-sm text-slate-400 text-center mb-4 leading-relaxed">{notif.message}</p>
        {notif.button1_text && notif.button1_url && <a href={notif.button1_url} target="_blank" rel="noopener noreferrer" className="block w-full py-3 bg-emerald-600 text-white font-semibold rounded-xl text-center mb-2">{notif.button1_text}</a>}
        <button onClick={onClose} className="w-full py-2.5 text-sm text-slate-500">Fechar</button>
      </div>
    </div>
  );
}

// ============= MAIN =============
export default function ClientePortalPage() {
  const [token, setToken] = useState(localStorage.getItem('cliente-token'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('home');
  const [notif, setNotif] = useState(null);
  const [notifList, setNotifList] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // PWA Setup
  useEffect(() => {
    const existingManifest = document.querySelector('link[rel="manifest"]');
    const clienteManifest = document.createElement('link');
    clienteManifest.rel = 'manifest'; clienteManifest.href = '/manifest-cliente.json';
    if (existingManifest) existingManifest.remove();
    document.head.appendChild(clienteManifest);
    let metaTheme = document.querySelector('meta[name="theme-color"]');
    if (!metaTheme) { metaTheme = document.createElement('meta'); metaTheme.name = 'theme-color'; document.head.appendChild(metaTheme); }
    metaTheme.content = '#060a13';
    let metaCapable = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (!metaCapable) { metaCapable = document.createElement('meta'); metaCapable.name = 'apple-mobile-web-app-capable'; metaCapable.content = 'yes'; document.head.appendChild(metaCapable); }
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"][sizes="180x180"]');
    if (!appleTouchIcon) { appleTouchIcon = document.createElement('link'); appleTouchIcon.rel = 'apple-touch-icon'; appleTouchIcon.sizes = '180x180'; appleTouchIcon.href = '/apple-touch-icon.png'; document.head.appendChild(appleTouchIcon); }
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw-cliente.js').catch(() => {});
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);
    return () => { window.removeEventListener('beforeinstallprompt', handler); };
  }, []);

  const setupWebPush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const { data: { public_key } } = await api.get('/api/web-push/vapid-key');
      if (!public_key) return;
      const urlBase64ToUint8Array = (base64String) => { const p = '='.repeat((4 - base64String.length % 4) % 4); const b64 = (base64String + p).replace(/-/g, '+').replace(/_/g, '/'); const raw = window.atob(b64); return Uint8Array.from([...raw].map(c => c.charCodeAt(0))); };
      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(public_key) });
      await api.post('/api/mobile/web-push-subscribe', { subscription: subscription.toJSON() });
    } catch {}
  };

  const handleInstall = async () => { if (!installPrompt) return; installPrompt.prompt(); const r = await installPrompt.userChoice; if (r.outcome === 'accepted') { setIsInstalled(true); toast.success('App instalado!'); } setInstallPrompt(null); };

  const loadData = async () => { try { const r = await api.get('/api/mobile/me'); setData(r.data); } catch (e) { if (e.response?.status === 401 || e.response?.status === 403) { localStorage.removeItem('cliente-token'); setToken(null); } } finally { setLoading(false); } };
  const loadNotifications = async () => {
    try {
      // Sininho: todas as recentes
      const r = await api.get('/api/mobile/notifications');
      setNotifList(r.data || []);
      // Auto-modal: só não lidas
      const unread = (r.data || []).filter(n => !n.read);
      if (unread.length > 0 && !notif) setNotif(unread[0]);
    } catch {}
  };
  const dismissNotif = async () => { if (notif) { try { await api.post(`/api/mobile/notifications/${notif.id}/read`); } catch {} } setNotif(null); };

  useEffect(() => {
    if (token) { loadData(); loadNotifications(); setTimeout(setupWebPush, 2000); const ni = setInterval(loadNotifications, 15000); const di = setInterval(loadData, 60000); return () => { clearInterval(ni); clearInterval(di); }; }
    else setLoading(false);
  }, [token]);

  const handleLogin = (d) => { setToken(d.token); setData(d.customer); setLoading(false); loadNotifications(); setTimeout(setupWebPush, 1500); };
  const handleLogout = () => { localStorage.removeItem('cliente-token'); setToken(null); setData(null); setView('home'); };

  if (!token) return <><Toaster position="top-center" richColors /><LoginView onLogin={handleLogin} /></>;
  if (loading) return <div className="min-h-screen bg-[#060a13] flex items-center justify-center"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="min-h-screen bg-[#060a13] flex items-center justify-center p-4"><div className="text-center"><p className="text-slate-400 mb-4">Erro ao carregar</p><button onClick={handleLogout} className="text-emerald-500 text-sm">Sair</button></div></div>;

  return (
    <div className="min-h-screen bg-[#060a13] pb-20">
      <Toaster position="top-center" richColors />
      <NotificationModal notif={notif} onClose={dismissNotif} />

      {/* Navbar */}
      <header className="sticky top-0 z-40 bg-[#0a1628] border-b border-[#14253d]">
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          <h1 className="text-sm font-bold text-white">Nexu<span className="text-emerald-500">Zap</span></h1>
          <div className="flex items-center gap-2">
            <button onClick={() => { setShowNotifs(true); loadNotifications(); }} className="w-8 h-8 rounded-full bg-[#0d1829] border border-[#1a2d4a] flex items-center justify-center hover:border-emerald-500/30 transition-colors relative">
              <Bell className="w-4 h-4 text-slate-500" />
              {notifList.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500" />}
            </button>
            <button onClick={handleLogout} className="text-xs text-slate-500 hover:text-red-400 transition-colors" data-testid="cliente-logout-btn">Sair</button>
          </div>
        </div>
      </header>

      {/* Notifications Panel */}
      {showNotifs && (
        <div className="fixed inset-0 z-50 bg-black/50" onClick={() => setShowNotifs(false)}>
          <div className="absolute top-14 right-4 w-80 max-h-[360px] bg-[#0d1829] border border-[#1a2d4a] rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-[#1a2d4a]"><p className="text-sm font-bold text-white">Notificações</p></div>
            <div className="max-h-[300px] overflow-y-auto">
              {notifList.length === 0 ? <p className="text-xs text-slate-500 text-center py-6">Nenhuma notificação</p>
               : notifList.slice(0, 20).map((n, i) => (
                <button key={n.id || i} onClick={() => { setNotif(n); setShowNotifs(false); }} className="w-full text-left px-4 py-3 border-b border-[#1a2d4a]/50 hover:bg-[#14253d] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.type === 'billing' ? 'bg-yellow-500/10' : 'bg-emerald-500/10'}`}>
                      {n.type === 'billing' ? <CreditCard className="w-3.5 h-3.5 text-yellow-500" /> : <Bell className="w-3.5 h-3.5 text-emerald-500" />}
                    </div>
                    <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-white truncate">{n.title}</p><p className="text-[11px] text-slate-500 truncate">{n.message || n.body}</p></div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-4">
        {view === 'home' && <DashboardView data={data} onNavigate={setView} />}
        {view === 'profile' && <ProfileView data={data} onBack={() => setView('home')} />}
        {view === 'payment' && <PaymentView data={data} onBack={() => setView('home')} />}
        {view === 'invoices' && <InvoicesView onBack={() => setView('home')} />}
        {view === 'speedtest' && <SpeedTestView onBack={() => setView('home')} />}
        {view === 'coupon' && <CouponView onBack={() => setView('home')} />}
        {view === 'birthday' && <BirthdayView onBack={() => { setView('home'); loadData(); }} />}
        {view === 'referral-received' && <ReferralReceivedView onBack={() => { setView('home'); loadData(); }} />}
        {view === 'referral-share' && <ReferralShareView onBack={() => { setView('home'); loadData(); }} />}
        {view === 'install-guide' && <InstallGuideView onBack={() => setView('home')} installPrompt={installPrompt} onInstall={handleInstall} />}
      </main>

      {/* Floating Tab Bar */}
      <div className="fixed bottom-4 left-0 right-0 z-40 flex justify-center">
        <div className="flex items-center gap-1 bg-[#0a1628] border border-[#14253d] rounded-full px-2 py-2 shadow-2xl">
          <button onClick={() => setView('home')} className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${view === 'home' ? 'bg-emerald-500/15' : 'hover:bg-[#14253d]'}`}>
            <Home className={`w-5 h-5 ${view === 'home' ? 'text-emerald-500' : 'text-slate-600'}`} />
          </button>
          <button onClick={() => setView('profile')} className={`w-11 h-11 rounded-full flex items-center justify-center transition-colors ${view === 'profile' ? 'bg-emerald-500/15' : 'hover:bg-[#14253d]'}`}>
            <User className={`w-5 h-5 ${view === 'profile' ? 'text-emerald-500' : 'text-slate-600'}`} />
          </button>
        </div>
      </div>

      {/* PWA Install */}
      {!isInstalled && view === 'home' && (
        <div className="max-w-lg mx-auto px-4 mb-4">
          <button onClick={installPrompt ? handleInstall : () => setView('install-guide')}
            className="w-full flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-emerald-600/10 to-blue-600/10 border border-emerald-500/20 hover:border-emerald-500/40 transition-colors">
            <Zap className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="flex-1 text-left"><p className="text-xs font-semibold text-white">Instalar App</p><p className="text-[10px] text-slate-500">Tela inicial</p></div>
            <ChevronRight className="w-4 h-4 text-emerald-500" />
          </button>
        </div>
      )}
    </div>
  );
}
